import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MDBInput } from "mdb-react-ui-kit";
import "./AutoCompleteInput.css";

interface AutoCompleteInputProps<T = string> {
    valueList: T[];
    value: T;
    setValue: (option: T) => void; // beware: It seems like, when setValue relies on a state/var from the mother components, this needs to be read from a ref to always have the correct value
    valueToString?: (value: T) => string;
    stringToValue?: (str: string) => T;
    label?: string;
    style?: React.CSSProperties;
    disabled?: boolean;
    allowInput?: boolean;
}

const defaultValueToString = <T,>(value: T): string => { return value as string }
const defaultStringToValue = <T,>(str: string): T => { return str as T }

const Portal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    return createPortal(children, document.body);
};

const AutoCompleteInput = <T,>({
    valueList,
    value,
    setValue,
    valueToString = defaultValueToString,
    stringToValue = defaultStringToValue,
    style,
    label,
    disabled,
    allowInput = true,
}: AutoCompleteInputProps<T>): JSX.Element => {

    if (typeof value !== "string" && valueToString === defaultValueToString) throw new Error("Prop 'valueToString()' needs to be specified for complex objects.");
    if (typeof value !== "string" && stringToValue === defaultStringToValue && allowInput) throw new Error("Prop 'stringToValue()' needs to be specified for complex objects, if custom inputs are allowed.");

    const valueRef = useRef(value);

    useEffect(() => {
        valueRef.current = value;
    }, [value])

    // input field
    const [inputValue, setInputValue] = useState(valueToString(value));
    const inputValueRef = useRef(inputValue);

    // options list / dropdown
    const [showOptions, setShowOptions] = useState<boolean>(false);
    const showOptionsRef = useRef(showOptions);
    const [options, setOptions] = useState(valueList.filter((entry) => valueToString(entry).toLowerCase().includes(inputValue.toLowerCase())).sort((a, b) => valueToString(a).localeCompare(valueToString(b))));
    const optionsRef = useRef(options);

    useEffect(() => {
        inputValueRef.current = inputValue;
        let valuesContaining = valueList.filter((entry) => valueToString(entry).toLowerCase().includes(inputValue.toLowerCase())).sort((a, b) => valueToString(a).localeCompare(valueToString(b)));
        setOptions(valuesContaining);
        let valuesStartingWith = valueList.filter((entry) => valueToString(entry).toLowerCase().startsWith(inputValue.toLowerCase())).sort((a, b) => valueToString(a).localeCompare(valueToString(b)));
        setSuggestion((allowInput && valuesStartingWith.length > 0) ? valueToString(valuesStartingWith[0]) : "");
    }, [inputValue]);

    useEffect(() => {
        optionsRef.current = options;
        if (!allowInput && options.length === 0) setIsInvalid(true);
        if (!allowInput && options.length > 0) setIsInvalid(false);
    }, [options]);

    useEffect(() => {
        showOptionsRef.current = showOptions;
        setSelected(-1);
    }, [showOptions]);
    
    // UI
    const inputSectionRef = useRef<HTMLDivElement>(null);
    const acOptionsWrapperRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState<number>(100);

    // other stuff
    const [selected, setSelected] = useState(-1);
    const selectedRef = useRef(selected);

    useEffect(() => {
        selectedRef.current = selected;
    }, [selected]);

    const [suggestion, setSuggestion] = useState("");
    const suggestionRef = useRef(suggestion);

    // const findSuggestion = () => {
    //     if (showOptionsRef.current && optionsRef.current && optionsRef.current.length > 0) {
    //         const suggestion = valueToString(optionsRef.current[0]);
    //         let dotIndexAfterA = suggestion.indexOf('.', inputValueRef.current.length);
    //         if (dotIndexAfterA !== -1 && inputValueRef.current.length < dotIndexAfterA + 1) return suggestion.substring(0, dotIndexAfterA + 1);
    //         else return suggestion;
    //     }
    //     return "";
    // }

    useEffect(() => {
        suggestionRef.current = suggestion;
    }, [suggestion]);

    const [isInvalid, setIsInvalid] = useState(false);

    const checkPosition = () => {
        if (inputSectionRef.current) setWidth(inputSectionRef.current.getBoundingClientRect().width);
    }

    useEffect(() => {
        // resize event listener to adapt the width of the dropdown
        checkPosition();
        window.addEventListener('resize', checkPosition);

        const handleKeyDown = (event: any) => {
            if (event.key === 'Tab' && showOptionsRef.current) {
                event.preventDefault();
                setInputValue(suggestionRef.current);
            }

            if (event.key === 'ArrowUp' && showOptionsRef.current) {
                let target = -1;
                if (selectedRef.current > 0) target = selectedRef.current - 1;
                else target = optionsRef.current.length - 1;
                if (target >= 0) {
                    setSelected(target);
                    const div = document.getElementById(`acOption_${target}`);
                    if (div) div.scrollIntoView({ block: "end", inline: "nearest" });
                }
            }

            else if (event.key === 'ArrowDown' && showOptionsRef.current) {
                let target = -1;
                if (selectedRef.current < optionsRef.current.length - 1) target = selectedRef.current + 1;
                else target = 0;
                if (target >= 0) {
                    setSelected(target);
                    const div = document.getElementById(`acOption_${target}`);
                    if (div) div.scrollIntoView({ block: "end", inline: "nearest" });
                }
            }

            else if (event.key === 'Enter' && showOptionsRef.current) {
                if (selectedRef.current >= 0) {
                    setValue(optionsRef.current[selectedRef.current]);
                    setInputValue(valueToString(optionsRef.current[selectedRef.current]));
                    setSelected(0); //resets the index to 0 as the input value is set to the selected index
                }
            }

            else if (event.key === "Escape") handleClick(event);
        }
        window.addEventListener('keydown', handleKeyDown);

        const handleClick = (event: any) => {
            if (acOptionsWrapperRef.current && !acOptionsWrapperRef.current.contains(event.target)) {
                setShowOptions(false);
                if (allowInput) setValue(stringToValue(inputValueRef.current));
                else setInputValue(valueToString(valueRef.current));
            }
        }
        window.addEventListener('mousedown', handleClick);

        return () => {
            window.removeEventListener('resize', checkPosition);
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('mousedown', handleClick);
        }
    }, []);

    return (
        <div style={style}>
            <div ref={inputSectionRef} className="inputSection" style={{ zIndex: showOptions ? "1062" : "auto" }}>
                <MDBInput className={`inputField ${isInvalid ? "is-invalid" : ""}`} label={label} type="text" value={inputValue} onChange={(e: any) => { setInputValue(e.target.value) }} onFocus={() => { setShowOptions(true); checkPosition() }} disabled={disabled} />
                <span className="suggestion">{suggestion}</span>
                {showOptions && <Portal>
                    <div ref={acOptionsWrapperRef} className="acOptionsWrapper" style={{
                        width: width + "px",
                        maxHeight: `min(300px, calc(100vh - ${inputSectionRef.current ? inputSectionRef.current.getBoundingClientRect().bottom : 0}px))`,
                        overflowY: "auto",
                        position: "absolute",
                        top: inputSectionRef.current ? inputSectionRef.current.getBoundingClientRect().bottom + "px" : "0px",
                        left: inputSectionRef.current ? inputSectionRef.current.getBoundingClientRect().left + "px" : "0px",
                        zIndex: 1062
                    }}>
                        {options.map((entry: T, index) => {
                            return <div className="acOption" id={`acOption_${index}`} key={index} onMouseEnter={() => setSelected(index)} onClick={() => { setValue(entry); setInputValue(valueToString(entry)); setShowOptions(false); }} style={selected === index ? { filter: "brightness(0.90)" } : {}} title={valueToString(entry)}>{valueToString(entry)}</div>
                        })}
                    </div>
                </Portal>}
            </div>
        </div>
    )
}

export default AutoCompleteInput;
